import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, Search, FileSearch, Kanban, Building2, MapPin, ArrowRight,
  CheckCircle2, Radar, MailPlus, ShieldCheck, Zap,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const cities = ["Montréal", "Laval", "Repentigny", "Lavaltrie", "Joliette", "Lanaudière", "Terrebonne", "Longueuil"];

const features = [
  { icon: Radar, title: "Détection intelligente", desc: "L'IA analyse en continu les propriétés commerciales et appels d'offres publics du Grand Montréal." },
  { icon: Sparkles, title: "Qualification IA", desc: "Chaque opportunité reçoit un score 0-100 avec preuves classées: FAIT · INFÉRENCE · ESTIMATION." },
  { icon: MailPlus, title: "Rédaction commerciale", desc: "Courriels, scripts d'appel et messages LinkedIn personnalisés, jamais envoyés sans votre approbation." },
  { icon: Kanban, title: "CRM intégré", desc: "Pipeline visuel de Nouveau lead à Contrat gagné, relances automatiques et notes centralisées." },
  { icon: FileSearch, title: "Veille SEAO", desc: "Suivi des appels d'offres publics avec alertes 'Fermeture proche' et correspondance avec votre profil." },
  { icon: ShieldCheck, title: "Conformité", desc: "Aucune fabrication de contacts. Chaque donnée expose sa source, sa date et son niveau de confiance." },
];

const pricing = [
  { name: "Gratuit", price: "0 $", desc: "Découvrir la plateforme", features: ["15 recherches / mois", "IA de base", "1 utilisateur"], cta: "Commencer" },
  { name: "Pro", price: "89 $", suffix: "/ mois", featured: true, desc: "Entrepreneurs indépendants", features: ["Recherches illimitées", "Génération IA complète", "CRM + relances", "Veille SEAO"], cta: "Essayer Pro" },
  { name: "Business", price: "249 $", suffix: "/ mois", desc: "Équipes de vente", features: ["Tout Pro", "5 utilisateurs", "Analytics avancées", "Priorité IA"], cta: "Contactez-nous" },
];

const faq = [
  { q: "Qu'est-ce qu'un contrat de nettoyage commercial ?", a: "Une entente contractuelle entre une entreprise de nettoyage et une organisation commerciale (bureaux, immeubles, écoles, etc.) pour des services d'entretien récurrents ou ponctuels." },
  { q: "Comment l'IA trouve-t-elle les opportunités ?", a: "L'IA analyse des sources publiques autorisées (appels d'offres, registres, sources ouvertes) et identifie les propriétés commerciales dont le profil correspond aux besoins d'entretien. Chaque lead expose sa source." },
  { q: "La plateforme garantit-elle des contrats ?", a: "Non. Aucune plateforme ne peut garantir la conversion d'un prospect en client. Nous fournissons des opportunités priorisées avec preuves — le travail commercial reste le vôtre." },
  { q: "Puis-je suivre les appels d'offres publics du Québec ?", a: "Oui. Notre moniteur d'appels d'offres suit les publications pertinentes (démo actuellement, intégration SEAO planifiée) avec alertes personnalisées." },
  { q: "Les courriels sont-ils envoyés automatiquement ?", a: "Jamais sans votre approbation explicite. L'IA rédige, vous éditez, prévisualisez et envoyez." },
];

export default function Landing() {
  return (
    <div className="min-h-screen text-foreground">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-cyan-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-slate-950" />
            </div>
            <span className="font-display font-bold text-sm tracking-tight">CLEANING CONTRACT AI</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#fonctionnalites" className="hover:text-foreground">Fonctionnalités</a>
            <a href="#couverture" className="hover:text-foreground">Couverture</a>
            <a href="#tarifs" className="hover:text-foreground">Tarifs</a>
            <a href="#faq" className="hover:text-foreground">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="landing-login-button">Connexion</Button>
            </Link>
            <Link to="/inscription">
              <Button size="sm" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" data-testid="landing-signup-button">
                Démarrer
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-gradient pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 mb-6 font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 mr-2 pulse-glow" /> Nouveau · Agent IA de prospection
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
                Trouvez plus de{" "}
                <span className="text-gradient-cyan">contrats de nettoyage commercial</span>{" "}
                avec l'IA
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
                Un agent commercial intelligent qui aide les entreprises de nettoyage à découvrir
                des opportunités qualifiées, des appels d'offres actifs et des prospects prêts
                à évaluer un service — dans le Grand Montréal et Lanaudière.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/inscription">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold" data-testid="hero-primary-cta">
                    Commencer à trouver des contrats <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="border-border hover:border-cyan-500/50" data-testid="hero-secondary-cta">
                    Explorer les opportunités
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-10 text-xs text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Sans engagement</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Données sourcées</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Interface française</div>
              </div>
            </div>

            {/* Hero preview */}
            <div className="relative">
              <div className="glass rounded-2xl p-6 shadow-2xl relative overflow-hidden">
                <div className="scan-line absolute inset-x-0 h-16 bg-gradient-to-b from-cyan-400/20 to-transparent pointer-events-none" />
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs text-muted-foreground font-mono">cleaning-contract-ai / opportunités</div>
                  <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Analyse IA en cours</Badge>
                </div>
                <div className="space-y-3">
                  {[
                    { name: "Tour Place Ville-Marie", city: "Montréal", score: 92, type: "OFFICE" },
                    { name: "CSSS Lanaudière — Joliette", city: "Joliette", score: 88, type: "TENDER" },
                    { name: "Centre Laval", city: "Laval", score: 85, type: "RETAIL" },
                    { name: "Complexe Industriel Repentigny", city: "Repentigny", score: 74, type: "INDUSTRIAL" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-card/60 border border-border/50">
                      <div className="h-10 w-10 rounded-md bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/20 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-cyan-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {r.city} · {r.type}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-display font-bold text-cyan-300">{r.score}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">SCORE IA</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
                  <div><div className="text-xs text-muted-foreground">Détectés</div><div className="font-display font-bold text-lg">148</div></div>
                  <div><div className="text-xs text-muted-foreground">Priorité haute</div><div className="font-display font-bold text-lg text-emerald-400">37</div></div>
                  <div><div className="text-xs text-muted-foreground">Appels d'offres</div><div className="font-display font-bold text-lg text-cyan-400">12</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="fonctionnalites" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-3">Fonctionnalités clés</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Tout ce qu'il faut pour développer votre carnet de clients
            </h2>
            <p className="text-muted-foreground">
              Une plateforme conçue avec et pour l'industrie du nettoyage commercial québécoise.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="bg-card border-border p-6 card-hover" data-testid={`feature-card-${i}`}>
                <div className="h-11 w-11 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-cyan-400" />
                </div>
                <h3 className="font-display font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section id="couverture" className="py-24 bg-card/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-3">Zones de couverture</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Grand Montréal & Lanaudière
            </h2>
            <p className="text-muted-foreground">
              Focus initial sur les marchés à forte densité commerciale, avec expansion prévue au Canada et à l'international.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {cities.map((c) => (
              <div key={c} className="px-5 py-2.5 rounded-full bg-card border border-border text-sm font-medium hover:border-cyan-500/40 transition-all" data-testid={`coverage-city-${c}`}>
                <MapPin className="inline h-3.5 w-3.5 mr-1.5 text-cyan-400" /> {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-3">Tarifs</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">Un forfait pour chaque étape</h2>
            <p className="text-muted-foreground">Commencez gratuitement, augmentez quand vous êtes prêt.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((p, i) => (
              <Card
                key={i}
                data-testid={`pricing-${p.name.toLowerCase()}`}
                className={`p-8 relative ${p.featured ? "bg-gradient-to-b from-cyan-500/10 to-transparent border-cyan-500/40" : "bg-card border-border"}`}
              >
                {p.featured && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-slate-950">Populaire</Badge>
                )}
                <div className="text-sm font-medium text-muted-foreground mb-2">{p.name}</div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-display text-4xl font-bold">{p.price}</span>
                  {p.suffix && <span className="text-sm text-muted-foreground">{p.suffix}</span>}
                </div>
                <p className="text-sm text-muted-foreground mb-6">{p.desc}</p>
                <ul className="space-y-2 mb-6">
                  {p.features.map((f, j) => (
                    <li key={j} className="text-sm flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/inscription">
                  <Button
                    className={`w-full ${p.featured ? "bg-cyan-500 hover:bg-cyan-400 text-slate-950" : ""}`}
                    variant={p.featured ? "default" : "outline"}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-card/30 border-t border-border">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-xs font-semibold uppercase tracking-wider text-cyan-400 mb-3">Foire aux questions</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Questions fréquentes</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border rounded-xl px-5">
                <AccordionTrigger className="text-left font-medium py-4" data-testid={`faq-question-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden hero-gradient">
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <Zap className="h-10 w-10 text-cyan-400 mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Prêt à trouver votre prochain contrat ?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">Créez votre compte en 60 secondes.</p>
          <Link to="/inscription">
            <Button size="lg" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold" data-testid="cta-signup-button">
              Créer mon compte gratuit <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-6 w-6 rounded bg-gradient-to-br from-cyan-400 to-emerald-500" />
            © 2026 Cleaning Contract AI — Québec, Canada
          </div>
          <div className="text-xs text-muted-foreground">Aucun contrat garanti. Chaque lead expose sa source.</div>
        </div>
      </footer>
    </div>
  );
}
